import React, { useCallback, useContext } from 'react';
import { Breadcrumb, Empty, Tabs, Row, Col } from 'antd';
import { SolutionOutlined } from '@ant-design/icons';
import AuthContext from '@packages/contexts/auth/authContext';
import { Link, useHistory } from 'react-router-dom';
import { NoRecordFound } from '../../components/Shared';
import { FORM_MODE } from '../../types/Form';
import { useClientFormContext } from './ClientFormContext';
import { isFormActionAllowedByPermission } from './utils/helpers';
import { Permission } from '../../types/Auth';
import ClientBaseForm from './ClientFormTabs/ClientBaseForm';
import ClientCredentialsForm from './ClientFormTabs/ClientCredentialsForm';
import ClientFleetsForm from './ClientFormTabs/ClientFleetsForm';
import ClientPriceMatrixForm from './ClientFormTabs/ClientPriceMatrixForm';
import ClientJsonTransformForm from './ClientFormTabs/ClientJsonTransformForm';
import ClientWebhookForm from './ClientFormTabs/ClientWebhookForm';
import ClientAutostackingForm from './ClientFormTabs/ClientAutostackingForm';
import SmartFleetChoiceForm from './ClientFormTabs/SmartFleetChoiceForm';
import ClientQuoteDenialToolForm from './ClientFormTabs/ClientQuoteDenialToolForm';
import ClientOnDemandForm from './ClientFormTabs/ClientOnDemandForm';
import ClientScheduledForm from './ClientFormTabs/ClientScheduledForm';
import {
    ScheduledFeatureContext,
    ScheduledFeatureProvider,
} from '../../components/Feature/Scheduled/ScheduledFeatureContext';

const { TabPane } = Tabs;

const ClientTabbedForm: React.FC = (): React.ReactElement => {
    const { client, formConfig, mode, isNoClientFound } = useClientFormContext();
    const { user } = useContext(AuthContext);
    const urlSearchParams = new URLSearchParams(window.location.search);
    const activeTab = urlSearchParams.get('tab') || 'client';
    const history = useHistory();

    const breadcrumbs = (
        <Breadcrumb className="mb-3">
            <Breadcrumb.Item>
                <SolutionOutlined className="d-inline-flex mr-1" />
                <Link to="/admin/clients" title="Clients">
                    Clients
                </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{formConfig.setupModes[mode].title}</Breadcrumb.Item>
            {!formConfig.isAddMode && <Breadcrumb.Item>{client?.name || 'new client'}</Breadcrumb.Item>}
        </Breadcrumb>
    );

    if (formConfig.isAddMode && !isFormActionAllowedByPermission(user, Permission.CLIENT_ADD)) {
        return (
            <>
                {breadcrumbs}
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Sorry, you are not allowed to perform this action."
                    className="p-5"
                >
                    <Link id="link-to-client-list" to="/admin/clients" title="Return to client list">
                        Return to client list
                    </Link>
                </Empty>
            </>
        );
    }

    const renderEditTabs = formConfig.isDetailMode || formConfig.isEditMode;

    if (isNoClientFound && (formConfig.isDetailMode || formConfig.isEditMode)) {
        return (
            <NoRecordFound message="No client found.">
                <Link id="link-to-add-client" to={`/admin/clients/${FORM_MODE.ADD}`} title="Add client">
                    Add client
                </Link>
            </NoRecordFound>
        );
    }

    const updateUrl = useCallback(
        (newActiveTab) => {
            let newUrl = `/admin/clients/${mode}`;
            if (client) {
                newUrl += `/${client.id}`;
                newUrl += `?tab=${newActiveTab}`;
            }
            history.push(newUrl);
        },
        [mode, client],
    );

    return (
        <div>
            <Row>{breadcrumbs}</Row>
            <Row style={{ display: 'grid' }}>
                <Tabs defaultActiveKey={activeTab} className="d-flex w-100" onChange={updateUrl}>
                    <TabPane tab="Client base form" key="client" forceRender>
                        <ClientBaseForm />
                    </TabPane>
                    {renderEditTabs && (
                        <>
                            <TabPane tab="Autostacking" key="autostacking" forceRender>
                                <ClientAutostackingForm />
                            </TabPane>
                            <TabPane tab="Webhook & Api forwarding" key="forwarding" forceRender>
                                <ClientWebhookForm />
                            </TabPane>
                            <TabPane tab="JsonTransform" key="jsonTransform" forceRender>
                                <ClientJsonTransformForm />
                            </TabPane>
                            <TabPane tab="Scheduled" key="scheduled" forceRender>
                                <ScheduledFeatureProvider>
                                    <ClientScheduledForm />
                                </ScheduledFeatureProvider>
                            </TabPane>
                            <TabPane tab="Fleets & ETAs" key="fleets" forceRender>
                                <ClientFleetsForm />
                            </TabPane>
                            <TabPane tab="Smart Fleet Choice" key="smart-fleet-choice" forceRender>
                                <SmartFleetChoiceForm />
                            </TabPane>
                            <TabPane tab="Stuart API credentials" key="credentials" forceRender>
                                <ClientCredentialsForm />
                            </TabPane>
                            <TabPane tab="Price Matrix" key="pricing" forceRender>
                                <ClientPriceMatrixForm />
                            </TabPane>
                            <TabPane tab="Quote Config" key="quotes" forceRender>
                                <ClientQuoteDenialToolForm />
                            </TabPane>
                            <TabPane tab="On demand features" key="on-demand" forceRender>
                                <ClientOnDemandForm />
                            </TabPane>
                        </>
                    )}
                </Tabs>
            </Row>
        </div>
    );
};

export default ClientTabbedForm;
